import { useEffect, useState } from 'react';
import { apiUrl, type Loadable } from '../../api/client';
import { StateBlock } from '../StateBlock';
import { BlockHistory, BLOCK_SERIES, type BlockPoint, type BlockSeriesKey } from '../charts/BlockHistory';

export interface BlockSeason {
  season: string;
  year: number;
  crop: string | null;
  prob: number | null;
  confidence: 'high' | 'medium' | 'low' | 'none' | null;
  passes: number;
  peak_ndvi: number | null;
  peak_date: string | null;
  first_green: string | null;
  senesced_by: string | null;
  max_ndwi: number | null;
  garlic_prob: number | null;
  reason: string | null;
  nearest_library: string | null;
  calendar_score: number | null;
}

export interface BlockRecord {
  pid: string;
  village: string;
  khasra: string;
  owner: string;
  father: string;
  acres: number;
  holding_acres: number;
  pixels: number;
  last_date: string | null;
  series: BlockPoint[];
  seasons: BlockSeason[];
}

type View = { key: BlockSeriesKey[]; label: string };

const VIEWS = [
  { key: ['ndvi'], label: 'Crop health' },
  { key: ['ndvi', 'ndwi'], label: 'Health + water' },
  { key: ['ndvi', 'ndmi', 'ndre'], label: 'Health + moisture + nitrogen' },
] as const satisfies readonly View[];

/**
 * The prebuilt record for one khasra: every pass since 2017, and what grew.
 *
 * Nothing here is computed when the panel opens. The archive was read once,
 * field by field, into a table; this reads one small file. That is the whole
 * reason a nine-year history can appear on a phone in a village in the time it
 * takes to tap.
 *
 * The crop line is deliberately the least confident thing on the screen. The
 * spectral reference library holds twelve crops and none of them is soybean,
 * garlic or gram - which is most of what this district plants - so a season is
 * named from the field's OWN sowing, peak and senescence dates against the
 * local crop calendar, and the library's nearest class is shown only as
 * evidence. Where neither is good enough the panel says so instead of naming a
 * crop, because a grower who is told "wheat" on his gram field stops reading.
 */
export function BlockRecordPanel({ pid }: { pid: string }): JSX.Element {
  const [state, setState] = useState<Loadable<BlockRecord>>({ status: 'idle' });
  const [view, setView] = useState(1);

  useEffect(() => {
    let cancelled = false;
    setState({ status: 'loading' });
    fetch(apiUrl(`/data/block/series/${pid}.json`))
      .then((r) => {
        if (r.status === 404) throw new Error('not-in-block');
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((data: BlockRecord) => {
        if (!cancelled) setState({ status: 'ready', data });
      })
      .catch((e: Error) => {
        if (cancelled) return;
        setState(
          e.message === 'not-in-block'
            ? {
                status: 'not_configured',
                requires: [],
                message:
                  'This farm is not in the 1,000-acre programme block, so no history has been prebuilt for it.',
              }
            : { status: 'error', message: e.message },
        );
      });
    return () => {
      cancelled = true;
    };
  }, [pid]);

  if (state.status !== 'ready') {
    return <StateBlock state={state} title="Field history" />;
  }

  const rec = state.data;
  const series = rec.series ?? [];
  const seasons = (rec.seasons ?? []).slice().reverse();
  const first = series[0] ?? null;
  const last = series.length ? (series[series.length - 1] ?? null) : null;
  const years = first && last ? Number(last.d.slice(0, 4)) - Number(first.d.slice(0, 4)) + 1 : 0;
  const wet = series.filter((p) => p.ndwi !== null && (p.ndwi as number) > 0).length;
  const active: View = VIEWS[view] ?? VIEWS[0];

  return (
    <section className="card card--wide">
      <h3 className="card__title">Field history</h3>
      <p className="card__subtitle">
        {series.length} satellite passes on this khasra&rsquo;s own {rec.pixels} pixels, over{' '}
        {years} {years === 1 ? 'year' : 'years'}.
      </p>

      <div className="stat-row">
        <div className="stat">
          <span className="stat__label">Last seen</span>
          <span className="stat__value">{last ? last.d : '—'}</span>
        </div>
        <div className="stat">
          <span className="stat__label">Crop health then</span>
          <span className="stat__value">{last && last.ndvi !== null ? last.ndvi.toFixed(2) : '—'}</span>
        </div>
        <div className="stat">
          <span className="stat__label">Standing water</span>
          <span className="stat__value">{wet ? `${wet} passes` : 'never seen'}</span>
        </div>
        <div className="stat">
          <span className="stat__label">Seasons on record</span>
          <span className="stat__value">{rec.seasons?.length ?? 0}</span>
        </div>
      </div>

      <div className="tabs">
        {VIEWS.map((v, i) => (
          <button
            key={v.label}
            type="button"
            className={i === view ? 'tab is-active' : 'tab'}
            onClick={() => setView(i)}
          >
            {v.label}
          </button>
        ))}
      </div>

      <BlockHistory points={series} keys={active.key} />

      <div className="chart__legend">
        {active.key.map((k) => (
          <span key={k} className="chart__legend-item">
            <i style={{ background: BLOCK_SERIES[k].colour }} />
            {BLOCK_SERIES[k].label}
          </span>
        ))}
      </div>

      <h4 className="card__title">Season by season</h4>
      {seasons.length ? (
        <table className="table">
          <thead>
            <tr>
              <th>Season</th>
              <th>Crop</th>
              <th className="num">Peak</th>
              <th>Confidence</th>
            </tr>
          </thead>
          <tbody>
            {seasons.map((s) => (
              <tr key={`${s.season}-${s.year}`}>
                <td>
                  {s.season} {s.year}
                  <span className="card__muted"> · {s.passes} pass{s.passes === 1 ? '' : 'es'}</span>
                </td>
                <td>
                  {s.crop ?? <em>not identified</em>}
                  {s.garlic_prob != null && s.garlic_prob > 0.5 ? <span className="card__muted"> · garlic-like curve</span> : null}
                  {s.reason ? <span className="table__note">{s.reason}</span> : null}
                </td>
                <td className="num">{s.peak_ndvi != null ? s.peak_ndvi.toFixed(2) : '—'}</td>
                <td>
                  <span className={`pill pill--${s.confidence ?? 'none'}`}>{s.confidence ?? 'none'}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <p className="card__empty">No season has been closed on this field yet.</p>
      )}

      <p className="card__note">
        A season is named from this field&rsquo;s own sowing, peak and senescence dates against the local
        crop calendar. The reference spectral library holds twelve crops and none of them is soybean,
        garlic or gram, so where its nearest class is a crop this district does not grow it is kept as
        evidence only and not printed as the answer.
      </p>
    </section>
  );
}
